/**
 * Her card, rendered by us, inside someone else's application.
 *
 * WHY THIS IS A PAGE AND NOT AN ENDPOINT
 * §3.12 was decided on 19 October as a branded component we serve rather than
 * data a partner renders. The difference is where her card lives: this way the
 * balance and the transactions are drawn by our code, in our frame, and the
 * partner's application never holds them.
 *
 * WHY IT TAKES A TOKEN AND NOT A LOGIN
 * She has no session with us — she is inside the partner's app. The partner's
 * SERVER mints a short-lived pass naming only her, and points an iframe here.
 * Their API key stays on their server, which is the whole reason the component
 * exists in this shape.
 *
 * WHY IT MUST LIVE UNDER /public/
 * frame-ancestors is 'self' across the web app except for that prefix. Served
 * anywhere else it could not be framed at all, and would fail silently inside
 * the partner's page — a blank box with nothing in the console to explain it.
 *
 * NOTHING IS STORED. No localStorage, no analytics, no scroll restoration. The
 * token is in the URL and the URL belongs to a frame we do not own.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface EmbeddedCard {
  last4: string | null;
  network: string | null;
  card_type: string | null;
  status: string | null;
  available_balance: number | null;
  expires_on: string | null;
}

interface EmbeddedTransaction {
  merchant: string | null;
  amount: number | null;
  status: string | null;
  occurred_on: string | null;
}

interface EmbeddedCardView {
  member_first_name: string | null;
  card: EmbeddedCard | null;
  transactions: EmbeddedTransaction[];
  no_card: boolean;
}

function money(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default function PublicCardEmbed() {
  const { t } = useTranslation();
  const [search] = useSearchParams();
  const token = search.get('token') || '';

  const [view, setView] = useState<EmbeddedCardView | null>(null);
  const [expired, setExpired] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!token) {
      setExpired(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/partner/v1/card-embed?token=${encodeURIComponent(token)}`, {
          // No credentials: this frame has no session with us and must not
          // borrow one from a cookie that happens to be there.
          credentials: 'omit',
          headers: { Accept: 'application/json' },
        });
        if (cancelled) return;
        if (res.status === 403) {
          setExpired(true);
          return;
        }
        if (!res.ok) {
          setFailed(true);
          return;
        }
        setView(await res.json());
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Three outcomes, three messages. An expired pass is ordinary and is HER
  // app's job to fix by reopening; a failure is ours. Collapsing them into one
  // blank state is what makes an embedded component impossible to support.
  if (expired) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t('card.expired', 'This view has expired. Close it and open your card again.')}
      </div>
    );
  }
  if (failed) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t('card.failed', 'We could not load your card just now. Please try again shortly.')}
      </div>
    );
  }
  if (!view) {
    return <div className="p-6 text-sm text-muted-foreground">{t('card.loading', 'Loading your card…')}</div>;
  }

  if (view.no_card || !view.card) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        {t('card.none', 'You do not have a card yet. One is issued when your first payment is approved.')}
      </div>
    );
  }

  const c = view.card;

  return (
    <div className="space-y-4 p-4">
      <div className="rounded-xl border bg-gradient-to-br from-slate-800 to-slate-700 p-5 text-white">
        <p className="text-xs uppercase tracking-wider text-white/70">
          {t('card.balance', 'Available balance')}
        </p>
        <p className="mt-1 text-3xl font-semibold tabular-nums">{money(c.available_balance)}</p>
        <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-white/80">
          {/* Four digits. Never the number — it is not in the response at all. */}
          <span className="tabular-nums">•••• {c.last4 || '––––'}</span>
          {c.network && <span>{c.network}</span>}
          {c.expires_on && (
            <span className="tabular-nums">
              {t('card.expires', 'exp')} {c.expires_on.slice(0, 7)}
            </span>
          )}
        </div>
        {c.status && c.status !== 'ACTIVE' && (
          <p className="mt-3 inline-block rounded bg-white/15 px-2 py-0.5 text-xs">{c.status}</p>
        )}
      </div>

      <div>
        <h2 className="mb-2 text-sm font-medium">{t('card.recent', 'Recent activity')}</h2>
        {view.transactions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('card.noActivity', 'Nothing yet. Purchases appear here as they happen.')}
          </p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {view.transactions.map((tx, i) => (
              <li key={i} className="flex items-baseline justify-between gap-3 p-3 text-sm">
                <span className="min-w-0">
                  <span className="block truncate">{tx.merchant || t('card.purchase', 'Purchase')}</span>
                  {tx.occurred_on && (
                    <span className="block text-xs text-muted-foreground tabular-nums">
                      {tx.occurred_on.slice(0, 10)}
                    </span>
                  )}
                </span>
                <span className="shrink-0 tabular-nums">{money(tx.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
